import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalAccountsAPI } from "../api/external-accounts";
import type {
  CalDAVSetupData,
  CreateExternalAccountData,
  ExternalAccount,
  UpdateExternalAccountData,
} from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";

export const externalAccountKeys = {
  all: ["external_accounts"] as const,
  lists: () => [...externalAccountKeys.all, "list"] as const,
  list: (clientId: string) =>
    [...externalAccountKeys.lists(), clientId] as const,
  detail: (clientId: string, id: string) =>
    [...externalAccountKeys.all, "detail", clientId, id] as const,
};

export function useExternalAccounts() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<ExternalAccount[]>({
    queryKey: externalAccountKeys.list(clientId),
    queryFn: () => ExternalAccountsAPI.getAccounts(clientId),
    staleTime: 30 * 1000,
    enabled: !!clientId,
    refetchOnWindowFocus: true,
  });
}

export function useExternalAccount(id: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<ExternalAccount>({
    queryKey: externalAccountKeys.detail(clientId, id),
    queryFn: () => ExternalAccountsAPI.getAccount(clientId, id),
    enabled: !!clientId && !!id,
    refetchOnWindowFocus: true,
  });
}

export function useCreateExternalAccount() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExternalAccountData) =>
      ExternalAccountsAPI.createAccount(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.list(clientId),
      });
    },
  });
}

export function useUpdateExternalAccount() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateExternalAccountData;
    }) => ExternalAccountsAPI.updateAccount(clientId, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.detail(clientId, variables.id),
      });
    },
  });
}

export function useDeleteExternalAccount() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ExternalAccountsAPI.deleteAccount(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.list(clientId),
      });
    },
  });
}

export function useVerifyAccess() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useMutation({
    mutationFn: (id: string) => ExternalAccountsAPI.verifyAccess(clientId, id),
  });
}

export function useRenewAccess() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ExternalAccountsAPI.renewAccess(clientId, id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.detail(clientId, id),
      });
    },
  });
}

export function useSetupCalDAV() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CalDAVSetupData) =>
      ExternalAccountsAPI.setupCalDAV(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.list(clientId),
      });
    },
  });
}

export function useSetupICS() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { url: string; name: string }) =>
      ExternalAccountsAPI.setupICS(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.list(clientId),
      });
    },
  });
}
